import { IconName } from '../icons';
import { FEATURE_FLAGS } from '../../config/featureFlags';

export type NavItem = {
    label: string,
    /** Route to link to - the `generateRoute` path in App.tsx minus the `/*`. */
    path: string,
    icon: IconName,
    /** Empty = visible to everyone. Mirrors Draw's generateLink(accessGroups, ...). */
    accessGroups: string[],
}

/**
 * The navigation entries, in the exact order the old `Draw` rendered them:
 * My Profile first, then alphabetical by label.
 *
 * The flat alphabetical order is deliberate and load-bearing - it is muscle
 * memory for existing users - so the five-section grouping in
 * `src/design/shell/navConfig.ts` is NOT adopted here. Only its icon choices
 * are reused, with two collisions resolved: navConfig double-booked `build`
 * (Shop Plan + Tools Register) and `checklistRtl` (I/O Checklists + Task
 * Assignment), which is harmless beside a label but defeats an icon-only rail.
 */
export const NAV_ITEMS: NavItem[] = [
    { label: 'My Profile', path: '/my-profile', icon: 'accountCircle', accessGroups: [] },
    { label: 'Access Control', path: '/access-control', icon: 'lock', accessGroups: ['Developers', 'Access Control Admin'] },
    { label: 'Bay Handover', path: '/bay-handover', icon: 'send', accessGroups: ['Bay Handover View', 'Bay Handover Edit'] },
    { label: 'Capability', path: '/capability', icon: 'allInclusive', accessGroups: ['Capability Edit', 'Capability View'] },
    { label: 'Certificates', path: '/certificates', icon: 'menuBook', accessGroups: ['Certificates View', 'Certificates Create', 'Certificates Approver'] },
    { label: 'Clothes Requests', path: '/clothing-request', icon: 'work', accessGroups: ['Clothes Request', 'Clothes Request - View'] },
    { label: 'Courses', path: '/courses', icon: 'school', accessGroups: ['Course Managers'] },
    { label: 'Extra Payments', path: '/extra-payments', icon: 'attachMoney', accessGroups: ['Extra Payment User', 'Extra Payment Supervisor', 'Extra Payment Admin'] },
    { label: 'Hangar Overview', path: '/hangar-overview', icon: 'airlinesRounded', accessGroups: ['Hangar Overview'] },
    { label: 'Hangar Plan', path: '/HangarPlan', icon: 'scheduleOutlined', accessGroups: ['Hangar Plan View', 'Hangar Plan Edit'] },
    { label: 'I/O Checklists', path: '/checklists', icon: 'checkBox', accessGroups: ['Checklist Edit', 'Checklist View'] },
    { label: 'Line KEF', path: '/line-kef', icon: 'flightTakeoff', accessGroups: ['Line Planner', 'Line Planner View', 'Line Supervisor Edit', 'Line Supervisor View'] },
    { label: 'Line RKV', path: '/line-rkv', icon: 'localAirport', accessGroups: ['Rvk Line Edit', 'Rvk Line View'] },
    { label: 'Line Planner', path: '/line-planner', icon: 'formatListNumbered', accessGroups: ['Line Planner Handover Edit', 'Line Planner Handover View'] },
    { label: 'Lite', path: '/lite', icon: 'phoneAndroidOutlined', accessGroups: ['Lite Edit', 'Lite View'] },
    ...(FEATURE_FLAGS.maintenanceControl
        ? [{ label: 'Maintenance Control', path: '/maintenance-control', icon: 'alarmOn' as IconName, accessGroups: ['Maintenance Control Edit', 'Maintenance Control View'] }]
        : []),
    { label: 'Mechanic Info', path: '/skills', icon: 'person', accessGroups: ['Mechanic Skills Edit'] },
    { label: 'Request Leave', path: '/vacations', icon: 'directionsWalk', accessGroups: ['Vacation User', 'Vacation Admin'] },
    { label: 'Security', path: '/security', icon: 'visibility', accessGroups: ['Security Edit', 'Security View'] },
    { label: 'Shop Plan', path: '/ShopPlan', icon: 'accessTime', accessGroups: ['Shop Plan View', 'Shop Plan Edit'] },
    { label: 'Staff Management', path: '/staff-management', icon: 'groups', accessGroups: ['Staff Management Edit', 'Staff Management View'] },
    { label: 'Task Assignment', path: '/task-assignment', icon: 'checklistRtl', accessGroups: ['Task Assignment Edit', 'Task Assignment View'] },
    { label: 'Tools Register', path: '/tool-register', icon: 'build', accessGroups: ['Tool Register'] },
    { label: 'Tools Stock', path: '/tool-stock', icon: 'shoppingCart', accessGroups: ['Tool Stock View', 'Tool Stock'] },
    { label: 'Warranty', path: '/warranty', icon: 'description', accessGroups: ['Warranty Edit', 'Warranty View'] },
    { label: 'World Tools', path: '/world-tools', icon: 'public', accessGroups: ['World Tools Edit', 'World Tools View'] },
];

/**
 * Routes that render without any app chrome - kiosk screens and the print
 * view. These used to hide the header by reaching into the DOM
 * (`document.getElementById("header")`), which breaks the moment the header
 * stops existing; the shell owns the decision now.
 */
export const CHROMELESS_PATHS = ['/yodeck', '/LoanedToolsForToLong', '/flight-schedule-view'];

export const isChromeless = (pathname: string): boolean =>
    CHROMELESS_PATHS.some(p => pathname.toLowerCase().includes(p.toLowerCase()));
